import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import Layout  from '../components/Layout'
import { filesApi, emailListApi, reportsApi, ScheduleType, errorMsg } from '../api/client'

const C = { teal: '#2B4C5E', yellow: '#F2B705', muted: '#6B8A91', light: '#C7D9DB' }

const SCHEDULE_TYPES: { value: ScheduleType; label: string; desc: string }[] = [
  { value: 'weekly',   label: 'Weekly',              desc: 'Runs on selected days each week' },
  { value: 'daily',    label: 'Daily',               desc: 'Runs every day including Sunday' },
  { value: 'monthly',  label: 'Monthly',             desc: 'Runs on a specific day each month' },
  { value: 'multiple', label: 'Multiple Times / Day', desc: 'Runs at multiple times on selected days' },
]

const DAY_RANGES = [
  { value: 'MON-SAT', label: 'Mon – Sat' },
  { value: 'MON-FRI', label: 'Mon – Fri' },
  { value: 'MON-SUN', label: 'Every Day' },
]

export default function UploadPage() {
  const { getToken, ready } = useAuth()
  const navigate     = useNavigate()

  const [scripts,    setScripts]    = useState<string[]>([])
  const [emailLists, setEmailLists] = useState<string[]>([])
  const [uploading,  setUploading]  = useState(false)
  const [saving,     setSaving]     = useState(false)
  const [msg,        setMsg]        = useState('')
  const [isErr,      setIsErr]      = useState(false)
  const [dragOver,   setDragOver]   = useState(false)

  const [form, setForm] = useState({
    name:           '',
    description:    '',
    script_key:     '',
    email_list_key: 'config/email_list.xlsx',
    active:         true,
  })

  const [sched, setSched] = useState({
    schedule_type:         'weekly' as ScheduleType,
    schedule_times:        ['10:00'],
    schedule_days:         'MON-SAT',
    schedule_day_of_month: 1,
  })

  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (ready) loadOptions() }, [ready])

  async function loadOptions() {
    await getToken()
    const [fs, es] = await Promise.all([filesApi.list(), emailListApi.list()])
    setScripts(fs.data.files.map(f => f.key))
    setEmailLists(es.data.files.map(f => f.key))
  }

  async function handleScriptUpload(file: File) {
    if (!file.name.endsWith('.py')) { flash('Only .py files', true); return }
    setUploading(true)
    await getToken()
    const r = await filesApi.upload(file)
    const key = r.data.key
    setScripts(prev => [...new Set([...prev, key])])
    setForm(f => ({ ...f, script_key: key, name: f.name || file.name.replace('.py', '') }))
    flash(`Uploaded: ${file.name}`)
    setUploading(false)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleScriptUpload(file)
  }

  function addTime() {
    setSched(s => ({ ...s, schedule_times: [...s.schedule_times, '10:00'] }))
  }
  function removeTime(i: number) {
    setSched(s => ({ ...s, schedule_times: s.schedule_times.filter((_, idx) => idx !== i) }))
  }
  function updateTime(i: number, val: string) {
    setSched(s => {
      const times = [...s.schedule_times]; times[i] = val
      return { ...s, schedule_times: times }
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name || !form.script_key) { flash('Name and script required', true); return }
    if (sched.schedule_times.length === 0) { flash('At least one schedule time required', true); return }
    setSaving(true)
    try {
      await getToken()
      await reportsApi.create({ ...form, ...sched, schedule_time: sched.schedule_times[0] })
      flash('Report scheduled!')
      setTimeout(() => navigate('/'), 1200)
    } catch (e: any) {
      flash(errorMsg(e, 'Save failed'), true)
    } finally {
      setSaving(false)
    }
  }

  function flash(text: string, err = false) {
    setIsErr(err); setMsg(text); setTimeout(() => setMsg(''), 4000)
  }

  return (
    <Layout>
      <main style={{ padding: '24px 20px', maxWidth: 800 }}>

        {msg && (
          <div style={{
            position: 'fixed', top: 80, right: 24,
            background: isErr ? '#7f1d1d' : C.teal, color: '#fff',
            padding: '12px 20px', borderRadius: 8, zIndex: 2000,
            borderLeft: `4px solid ${isErr ? '#ef4444' : C.yellow}`,
          }}>{msg}</div>
        )}

        <div style={{ marginBottom: 32 }}>
          <h1 style={{ color: C.teal, fontSize: 24, fontWeight: 700 }}>Add Report</h1>
          <p style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
            Upload a Python script and configure its automated schedule
          </p>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Script upload */}
          <Section title="1. Upload Python Script">
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border:       `2px dashed ${dragOver ? C.yellow : '#d1d5db'}`,
                borderRadius: 10, padding: '40px 24px',
                textAlign:    'center', cursor: 'pointer',
                background:   dragOver ? 'rgba(235,191,0,0.04)' : '#fafafa',
                transition:   'all 0.15s',
              }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🐍</div>
              <div style={{ color: C.teal, fontWeight: 600 }}>
                {uploading ? 'Uploading…' : 'Drop .py file here or click to browse'}
              </div>
              <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Python scripts only</div>
              <input ref={fileRef} type="file" accept=".py" style={{ display: 'none' }}
                onChange={e => e.target.files?.[0] && handleScriptUpload(e.target.files[0])} />
            </div>
            {scripts.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Or select existing script</label>
                <select value={form.script_key}
                  onChange={e => setForm(f => ({ ...f, script_key: e.target.value }))}
                  style={inputStyle}>
                  <option value="">— select —</option>
                  {scripts.map(s => <option key={s} value={s}>{s.split('/').pop()}</option>)}
                </select>
              </div>
            )}
          </Section>

          {/* Report details */}
          <Section title="2. Report Details">
            <label style={labelStyle}>Report Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Login MTD Summary" style={inputStyle} required />
            <label style={{ ...labelStyle, marginTop: 16 }}>Description</label>
            <textarea value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What does this report show?" rows={3}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </Section>

          {/* Schedule */}
          <Section title="3. Schedule (IST)">
            {/* Type selector */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 20 }}>
              {SCHEDULE_TYPES.map(st => (
                <div key={st.value}
                  onClick={() => setSched(s => ({ ...s, schedule_type: st.value }))}
                  style={{
                    border:       `2px solid ${sched.schedule_type === st.value ? C.yellow : '#e5e7eb'}`,
                    borderRadius: 8, padding: '12px 16px', cursor: 'pointer',
                    background:   sched.schedule_type === st.value ? 'rgba(242,183,5,0.06)' : '#fff',
                    transition:   'all 0.15s',
                  }}>
                  <div style={{ fontWeight: 600, color: C.teal, fontSize: 13 }}>{st.label}</div>
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{st.desc}</div>
                </div>
              ))}
            </div>

            {/* Day range (weekly / multiple) */}
            {(sched.schedule_type === 'weekly' || sched.schedule_type === 'multiple') && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Run on</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {DAY_RANGES.map(d => (
                    <button key={d.value} type="button"
                      onClick={() => setSched(s => ({ ...s, schedule_days: d.value }))}
                      style={{
                        padding: '8px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                        border: `1.5px solid ${sched.schedule_days === d.value ? C.yellow : '#d1d5db'}`,
                        background: sched.schedule_days === d.value ? 'rgba(242,183,5,0.08)' : '#fff',
                        color: sched.schedule_days === d.value ? C.teal : C.muted,
                        fontWeight: sched.schedule_days === d.value ? 600 : 400,
                      }}>{d.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Daily — show day range too */}
            {sched.schedule_type === 'daily' && (
              <div style={{ marginBottom: 16, color: C.muted, fontSize: 13 }}>
                Runs every day (Mon – Sun)
              </div>
            )}

            {/* Day of month (monthly) */}
            {sched.schedule_type === 'monthly' && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Day of month</label>
                <input type="number" min={1} max={28}
                  value={sched.schedule_day_of_month}
                  onChange={e => setSched(s => ({ ...s, schedule_day_of_month: parseInt(e.target.value) || 1 }))}
                  style={{ ...inputStyle, width: 100 }} />
                <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>
                  Use 1–28 to avoid months with fewer days.
                </div>
              </div>
            )}

            {/* Time pickers */}
            <label style={labelStyle}>
              {sched.schedule_type === 'multiple' ? 'Run times (IST)' : 'Run time (IST)'}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sched.schedule_times.map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="time" value={t}
                    onChange={e => updateTime(i, e.target.value)}
                    style={{ ...inputStyle, width: 160 }} required />
                  {sched.schedule_type === 'multiple' && sched.schedule_times.length > 1 && (
                    <button type="button" onClick={() => removeTime(i)} style={smallBtnStyle('#fff1f2', '#dc2626')}>
                      − Remove
                    </button>
                  )}
                </div>
              ))}
              {sched.schedule_type === 'multiple' && (
                <button type="button" onClick={addTime}
                  style={{ ...smallBtnStyle('#f0fdf4', '#16a34a'), alignSelf: 'flex-start' }}>
                  + Add Time
                </button>
              )}
            </div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 8 }}>
              {schedSummary(sched)}
            </div>
          </Section>

          {/* Email list */}
          <Section title="4. Email Recipients">
            <label style={labelStyle}>Email list file</label>
            <select value={form.email_list_key}
              onChange={e => setForm(f => ({ ...f, email_list_key: e.target.value }))}
              style={inputStyle}>
              {emailLists.length === 0 && (
                <option value="config/email_list.xlsx">config/email_list.xlsx (default)</option>
              )}
              {emailLists.map(k => <option key={k} value={k}>{k.split('/').pop()}</option>)}
            </select>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>
              Upload new email lists via the Email Lists page.
            </div>
          </Section>

          {/* Active toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
            <input type="checkbox" id="active" checked={form.active}
              onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
              style={{ width: 18, height: 18, accentColor: C.yellow }} />
            <label htmlFor="active" style={{ color: C.teal, fontWeight: 600, cursor: 'pointer' }}>
              Activate immediately after saving
            </label>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" disabled={saving} style={{
              background: C.yellow, color: C.teal, border: 'none',
              padding: '12px 32px', borderRadius: 8, fontSize: 15,
              fontWeight: 700, cursor: 'pointer',
            }}>
              {saving ? 'Saving…' : 'Save & Schedule'}
            </button>
            <button type="button" onClick={() => navigate('/')} style={{
              background: '#f3f4f6', color: C.muted, border: 'none',
              padding: '12px 24px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </form>
      </main>
    </Layout>
  )
}

function schedSummary(s: { schedule_type: ScheduleType; schedule_times: string[]; schedule_days: string; schedule_day_of_month: number }): string {
  const times = s.schedule_times.join(' & ') + ' IST'
  if (s.schedule_type === 'daily')    return `Every day at ${times}`
  if (s.schedule_type === 'monthly')  return `Day ${s.schedule_day_of_month} of every month at ${times}`
  const dayLabel = { 'MON-SAT': 'Mon–Sat', 'MON-FRI': 'Mon–Fri', 'MON-SUN': 'every day' }[s.schedule_days] || s.schedule_days
  return `${dayLabel} at ${times}`
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '24px',
      marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    }}>
      <div style={{
        color: C.teal, fontWeight: 700, fontSize: 14,
        marginBottom: 16, paddingBottom: 12,
        borderBottom: `2px solid ${C.yellow}`, display: 'inline-block',
      }}>{title}</div>
      {children}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6,
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px',
  border: '1px solid #d1d5db', borderRadius: 6,
  fontSize: 14, color: C.teal, outline: 'none',
}
function smallBtnStyle(bg: string, color: string): React.CSSProperties {
  return {
    padding: '7px 14px', borderRadius: 6, border: `1px solid ${color}20`,
    background: bg, color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  }
}
